import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { IAlbum } from "../../../hooks/albums";
import {
  Box,
  CardActionArea,
  IconButton,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip
} from "@mui/material";
import { ModifyGenericAttributeDialog } from "../../attributes/ModifyGenericAttributeDialog";
import { strings } from "../../../localization/LocalizedStrings";
import { useUpdateAlbum } from "../../../hooks/albums/useUpdateAlbum";
import { AlertDialog } from "../../AlertDialog";
import { ArtistsDesktop } from "../../attributes/artists/ArtistsDesktop";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { ArtistDialog } from "../../attributes/artists/ArtistDialog";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CustomLoadingIndicator } from "../../CustomLoadingIndicator";
import { useIsMobile } from "../../../hooks/isMobile";
import { useContextMenu } from "../../../hooks/contextMenu";
import {
  useCollectionManager,
  useCurrentPrivateCollection
} from "../../../hooks/collections/useCollectionManager";
import { CollectionHelper } from "../../../models/CollectionHelper";
import { useShareLink } from "../../../hooks/share/useShareLink";
import { IArtist } from "../../../hooks/artists";
import { AttributeDialog } from "../../attributes/AttributeDialog";
import { Icon, IconType } from "../../../icons";
import { useResettableDialog } from "../../../hooks/resettableDialog/useResettableDialog";

export const AlbumHeaderListSection: FunctionComponent<IAlbumHeaderListSectionProps> = ({
  isPublic,
  album,
  onNameUpdated,
  onArtistUpdated
}) => {
  const isMobile = useIsMobile();
  const { updateAlbum } = useUpdateAlbum();
  const [currentPrivateCollection] = useCurrentPrivateCollection.useState();
  const { getCurrentCollection, isCurrentCollectionShareable } = useCollectionManager();
  const {
    state: nameDialogState,
    key: nameDialogKey,
    openAdd: openNameDialog,
    close: closeNameDialog
  } = useResettableDialog();
  const [isArtistDialogOpen, setIsArtistDialogOpen] = useState(false);
  const [isArtistsDesktopOpen, setIsArtistsDesktopOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [shareLinkComponents, fetchShareLink, fetchPrivateLink, isShareLoading] = useShareLink(
    album.name
  );
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const updateAlbumArtist = async (artist: IArtist | null) => {
    setIsArtistsDesktopOpen(false);
    setIsLoading(true);
    const { body, status } = await updateAlbum({
      uuid: album.uuid,
      artistId: artist?.uuid ?? null
    });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    } else {
      toast.success(strings.album_update_success);
      album.artist = artist;
      onArtistUpdated();
    }
    setIsLoading(false);
  };
  return (
    <ListItem sx={{ padding: "0 !important" }}>
      <Stack spacing={0} flexGrow={1}>
        <Stack direction={"row"}>
          <CardActionArea disabled={isPublic} className={styles.albumName} onClick={openNameDialog}>
            {album.name}
          </CardActionArea>
          <Box className={styles.loadingDiv}>
            {isLoading || isShareLoading ? (
              <CustomLoadingIndicator className={styles.loadingIndicator} />
            ) : (
              <Stack sx={{ height: "100%" }} direction={"row"}>
                {isCurrentCollectionShareable(isPublic) &&
                  (isPublic ||
                    CollectionHelper.albumBelongsTo(currentPrivateCollection, album)) && (
                    <Tooltip title={strings.share_album} arrow disableInteractive>
                      <IconButton
                        className={styles.shareButton}
                        onClick={() => {
                          const collectionId = getCurrentCollection(isPublic)?.uuid;
                          if (!collectionId) return;
                          fetchShareLink(collectionId, album.uuid).then();
                        }}
                      >
                        <Icon className={styles.shareIcon} type={IconType.Share} />
                      </IconButton>
                    </Tooltip>
                  )}
                {!isPublic && (
                  <Tooltip title={strings.get_private_link} arrow disableInteractive>
                    <IconButton
                      className={styles.shareButton}
                      onClick={() => {
                        fetchPrivateLink(album.uuid).then();
                      }}
                    >
                      <Icon type={IconType.QRCode2} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
        <CardActionArea
          className={styles.artistName}
          onClick={event => {
            if (album.isBoxSet) {
              if (album.artist) {
                setIsArtistDialogOpen(true);
              }
              return;
            }
            if (album.artist) {
              if (isPublic) {
                setIsArtistDialogOpen(true);
              } else {
                handleContextMenu(event);
              }
            } else {
              setIsArtistsDesktopOpen(true);
            }
          }}
        >
          {album.artist?.name ?? strings.unknown}
        </CardActionArea>
      </Stack>
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsArtistDialogOpen(true);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.Artist} />
          </ListItemIcon>
          {strings.artist_view}
        </MenuItem>
        {!isPublic && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setIsArtistsDesktopOpen(true);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.EditSquare} />
            </ListItemIcon>
            {strings.change_artist}
          </MenuItem>
        )}
      </Menu>
      {album.artist && (
        <ArtistDialog
          artist={album.artist}
          isOpen={isArtistDialogOpen}
          onClose={() => setIsArtistDialogOpen(false)}
          canEdit={!isPublic}
        />
      )}
      <AttributeDialog open={isArtistsDesktopOpen} onClose={() => setIsArtistsDesktopOpen(false)}>
        <ArtistsDesktop
          handleClose={() => {
            setIsArtistsDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={album.artist?.uuid}
          onArtistSelected={updateAlbumArtist}
          onClearValue={() => updateAlbumArtist(null)}
        />
      </AttributeDialog>
      <ModifyGenericAttributeDialog
        key={nameDialogKey}
        title={strings.name_title}
        description={strings.name_detail}
        placeholder={strings.name_placeholder}
        missingText={strings.field_missing}
        value={album.name}
        canClear={false}
        open={nameDialogState.mode !== "closed"}
        onClose={closeNameDialog}
        isNumber={false}
        onChange={async newValue => {
          setIsLoading(true);
          const { body, status } = await updateAlbum({ uuid: album.uuid, name: newValue });
          if (!StatusCodesHelper.isSuccessful(status)) {
            setAlertMessage(body.message);
            setIsAlertOpen(true);
          } else {
            toast.success(strings.album_update_success);
            onNameUpdated(body);
          }
          setIsLoading(false);
        }}
      />
      {shareLinkComponents()}
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </ListItem>
  );
};

export interface IAlbumHeaderListSectionProps {
  isPublic: boolean;
  album: IAlbum;
  onNameUpdated: (album: IAlbum) => void;
  onArtistUpdated: () => void;
}
